import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Avatar from "react-avatar";
import { BounceLoader, ClipLoader } from "react-spinners";
import PhoneIcon from "@material-ui/icons/Phone";
import LockIcon from "@material-ui/icons/Lock";
import EmailIcon from "@material-ui/icons/Email";
import PersonIcon from "@material-ui/icons/Person";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EditIcon from "@material-ui/icons/Edit";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import PageLayout from "../PageLayout/PageLayout";
import { loginSuccess } from "../../redux/userRedux";
import { uploadProfileImage, updateMyProfile } from "../../api/profileApi";

import "./UserProfile.css";

const UserProfile = () => {
  const user = useSelector((state) => state.user.currentUser);
  const dispatch = useDispatch();
  const history = useHistory();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const passwordHandler = () => {
    history.push("/passwordResetReq");
  };

  // console.log('UserProfile - raw user from Redux:', user);

  // Support both email auth (user.result) and normalized phone/google auth (direct user)
  const userData = user?.result || user;

  const openFilePicker = () => {
    if (uploading) return;
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // allow re-selecting the same file
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      // 1. Upload to Cloudinary (unsigned) to get a hosted URL.
      const { url } = await uploadProfileImage(file);

      // 2. Persist to the user's profile via the authenticated backend endpoint.
      const data = await updateMyProfile({ profilePicture: url });

      // 3. Reflect the change in the stored user/token immediately.
      //    Merge onto the existing user so unrelated fields (createdAt, etc.)
      //    are preserved; loginSuccess normalizes the { user, accessToken } shape.
      const mergedUser = {
        ...userData,
        ...(data.user || {}),
        profilePicture: url,
      };
      dispatch(
        loginSuccess({
          user: mergedUser,
          accessToken: data.accessToken || userData?.accessToken,
        }),
      );
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not update your photo. Please try again.";
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  // console.log('UserProfile - processed userData:', userData);
  // console.log('UserProfile - userData.name:', userData?.name);
  // console.log('UserProfile - userData._id:', userData?._id);
  // console.log('UserProfile - Will show loader?', !userData || !userData.name);

  // Only show loader if there's truly no user data
  if (!userData) {
    return <BounceLoader size={50} color={"#FF5C16"} />;
  }

  // If user data exists but no name, still render with what we have
  // (name might be loading or missing)
  if (!userData.name) {
    return <BounceLoader size={50} color={"#FF5C16"} />;
  }

  return (
    <PageLayout
      icon={PersonIcon}
      title="My Profile"
      subtitle="Manage your personal information"
    >
      {/* Profile Card */}
      <div className="profile-card">
        {/* Avatar Section */}
        <div className="profile-avatar-section">
          <div className="avatar-wrapper">
            <Avatar
              name={userData?.name}
              src={userData?.profilePicture || undefined}
              size="90"
              round={true}
            />
            <button
              type="button"
              className="avatar-edit-btn"
              onClick={openFilePicker}
              disabled={uploading}
              aria-label="Change profile photo"
              title="Change photo"
            >
              {uploading ? (
                <ClipLoader size={16} color={"#ffffff"} />
              ) : (
                <PhotoCameraIcon style={{ fontSize: "18px" }} />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </div>
          {uploadError && (
            <p className="avatar-upload-error">{uploadError}</p>
          )}
          <h2 className="profile-name">{userData?.name}</h2>
          
          {/* Auth Badge */}
          <div className="auth-badges">
            {userData?.phoneNumber && (
              <div className="auth-badge phone-auth">
                <LockIcon style={{ fontSize: "16px" }} />
                <span>Phone Verified</span>
              </div>
            )}
            {userData?.googleId && (
              <div className="auth-badge google-auth">
                <LockIcon style={{ fontSize: "16px" }} />
                <span>Google Account</span>
              </div>
            )}
            {userData?.email && !userData?.googleId && !userData?.phoneNumber && (
              <div className="auth-badge email-auth">
                <LockIcon style={{ fontSize: "16px" }} />
                <span>Email Verified</span>
              </div>
            )}
          </div>
        </div>

        {/* Profile Information */}
        <div className="profile-info-section">
          <h3 className="section-title">Contact Information</h3>
          
          {/* Email */}
          {userData?.email && (
            <div className="profile-info-row">
              <div className="info-icon-wrapper">
                <EmailIcon className="info-icon" />
              </div>
              <div className="info-content">
                <label className="info-label">Email Address</label>
                <p className="info-value">{userData.email}</p>
              </div>
            </div>
          )}
          
          {/* Phone Number */}
          {userData?.phoneNumber && (
            <div className="profile-info-row">
              <div className="info-icon-wrapper">
                <PhoneIcon className="info-icon" />
              </div>
              <div className="info-content">
                <label className="info-label">Phone Number</label>
                <p className="info-value">{userData.phoneNumber}</p>
              </div>
            </div>
          )}
          
          {/* User Role */}
          {userData?.user_role && (
            <div className="profile-info-row">
              <div className="info-icon-wrapper">
                <PersonIcon className="info-icon" />
              </div>
              <div className="info-content">
                <label className="info-label">Account Type</label>
                <p className="info-value">{userData.user_role}</p>
              </div>
            </div>
          )}
          
          {/* Created Date */}
          {userData?.createdAt && (
            <div className="profile-info-row">
              <div className="info-icon-wrapper">
                <CalendarTodayIcon className="info-icon" />
              </div>
              <div className="info-content">
                <label className="info-label">Member Since</label>
                <p className="info-value">
                  {new Date(userData.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Change Password Button (only for email users) */}
        {userData?.email && !userData?.googleId && !userData?.phoneNumber && (
          <div className="profile-actions">
            <button
              className="change-password-btn"
              onClick={passwordHandler}
            >
              <EditIcon style={{ fontSize: "18px", marginRight: "8px" }} />
              Change Password
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default UserProfile;
